import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchHousingData, fetchImmigrationCategories } from '../api';
import { ArrowLeft, ExternalLink, ArrowDown, ArrowUp } from 'lucide-react';
import {
  ComposedChart, Bar, Line, Area, AreaChart, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

// ─────────────────────────────────────────────────────────────────────────────
// Curated population-growth data (StatCan Demographic Estimates Program).
// PR admissions alone understate growth — the real driver 2022-24 was
// temporary residents. These figures are total annual population change.
// Sources cited at the foot of the page.
// ─────────────────────────────────────────────────────────────────────────────
const POP_GROWTH = {
  2011: 379000, 2012: 387000, 2013: 404000, 2014: 384000, 2015: 358000,
  2016: 437000, 2017: 478000, 2018: 528000, 2019: 583000, 2020: 254000,
  2021: 444000, 2022: 826000, 2023: 1271872, 2024: 744324, 2025: -20000,
};

// Average persons per dwelling (StatCan). Used to convert housing starts
// into "people housed" capacity. Canada's figure sits around 2.4-2.5.
const PERSONS_PER_DWELLING = 2.4;

const C = {
  people:  '#C0392B',   // red — population pressure (the pushing plate)
  homes:   '#2E7D9A',   // teal — housing capacity (the resisting plate)
  gap:     '#C8873A',   // copper — the accumulated gap
  ink:     '#1a1a2e',
};

function fmt(n) {
  return Number(Math.round(n)).toLocaleString('en-CA');
}
function fmtShort(n) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

// ── Tooltips ──────────────────────────────────────────────────────────────────
const tipBox = {
  background: C.ink,
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: '#e8e4da',
  minWidth: 220,
};
const tipRow = (label, value, color) => (
  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(232,228,218,0.7)' }}>
      {color && <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block' }} />}
      {label}
    </span>
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

const FlowTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      {tipRow('People added', fmt(d.people), C.people)}
      {tipRow('Homes built', fmt(d.homes), C.homes)}
      {tipRow('People housed*', fmt(d.capacity), C.homes)}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 8, paddingTop: 8 }}>
        {tipRow('Yearly shortfall', fmt(d.shortfall), C.gap)}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(232,228,218,0.45)', marginTop: 6 }}>
        *homes built × {PERSONS_PER_DWELLING} persons/dwelling
      </div>
    </div>
  );
};

const GapTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      {tipRow('Cumulative gap', fmt(d.cumulative), C.gap)}
      <div style={{ fontSize: 11, color: 'rgba(232,228,218,0.45)', marginTop: 6 }}>
        People added beyond housing capacity since 2011
      </div>
    </div>
  );
};

// ── Section pill ────────────────────────────────────────────────────────────
function SectionPill({ href, color, label }) {
  return (
    <a href={href}
       className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {label}
    </a>
  );
}

// ── Tectonic hero graphic (CSS/SVG, no external assets) ───────────────────────
function PlatesHero({ ratio }) {
  // ratio = people added per home built in the latest peak year
  return (
    <svg viewBox="0 0 700 220" className="w-full" style={{ maxHeight: 240 }}>
      <defs>
        <linearGradient id="plateA" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C0392B" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#7B241C" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="plateB" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2E7D9A" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#1A4A5C" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="faultGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C8873A" stopOpacity="0" />
          <stop offset="50%" stopColor="#C8873A" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#C8873A" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Left plate — population, pushing right */}
      <g>
        <polygon points="0,60 330,60 350,110 330,160 0,160" fill="url(#plateA)" />
        <text x="40" y="100" fill="#fff" fontSize="13" fontFamily="serif" letterSpacing="2"
              style={{ textTransform: 'uppercase' }}>Population</text>
        <text x="40" y="125" fill="rgba(255,255,255,0.7)" fontSize="11">demand for homes →</text>
      </g>

      {/* Right plate — housing supply, resisting */}
      <g>
        <polygon points="700,60 370,60 350,110 370,160 700,160" fill="url(#plateB)" />
        <text x="560" y="100" fill="#fff" fontSize="13" fontFamily="serif" letterSpacing="2"
              style={{ textTransform: 'uppercase' }}>Housing</text>
        <text x="560" y="125" fill="rgba(255,255,255,0.7)" fontSize="11">← supply of homes</text>
      </g>

      {/* Fault line glow */}
      <rect x="344" y="50" width="12" height="120" fill="url(#faultGlow)">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.5s" repeatCount="indefinite" />
      </rect>

      {/* Pressure readout */}
      <text x="350" y="200" fill="#C8873A" fontSize="13" textAnchor="middle" fontFamily="serif">
        {ratio ? `${ratio.toFixed(1)} people added per home built` : ''}
      </text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TheSqueeze() {
  const [housing, setHousing] = useState(null);
  const [immigration, setImmigration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchHousingData().catch(() => null),
      fetchImmigrationCategories().catch(() => null),
    ]).then(([h, im]) => {
      setHousing(h);
      setImmigration(im);
      setLoading(false);
      if (!h && !im) setError(true);
    });
  }, []);

  // Build the merged year-by-year series
  const startsHistory = housing?.starts?.history ?? [];
  const startsByYear = {};
  startsHistory.forEach(h => { startsByYear[h.year] = h.total; });

  const years = Object.keys(POP_GROWTH).map(Number).sort((a, b) => a - b);

  const flow = years.map(year => {
    const people = POP_GROWTH[year];
    const homes = startsByYear[year] ?? null;
    const capacity = homes != null ? homes * PERSONS_PER_DWELLING : null;
    const shortfall = (homes != null) ? people - capacity : null;
    return { year: String(year), people, homes, capacity, shortfall };
  });

  // Cumulative gap (only years where we have both figures)
  let running = 0;
  const cumulative = flow
    .filter(d => d.shortfall != null)
    .map(d => {
      running += d.shortfall;
      return { year: d.year, cumulative: running, yearly: d.shortfall };
    });

  const latestCumulative = cumulative.length ? cumulative[cumulative.length - 1].cumulative : 0;
  const peakYear = flow.reduce((max, d) =>
    (d.shortfall ?? -Infinity) > (max.shortfall ?? -Infinity) ? d : max, flow[0]);
  const peakRatio = (peakYear?.homes) ? peakYear.people / peakYear.homes : null;

  // Equivalent housing deficit (homes that would have been needed)
  const homesNeeded = latestCumulative / PERSONS_PER_DWELLING;

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      {/* ── Banner ── */}
      <section className="bg-ink-800 text-cream-100 overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
          <Link to="/"
                className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition">
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Immigration × Housing</div>
          <h1 className="font-serif text-[56px] md:text-[72px] leading-[0.96] tracking-tight">
            The Squeeze
          </h1>
          <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
            Two forces grind against each other beneath Canada's affordability crisis:
            the number of people arriving, and the number of homes being built to house them.
            When one plate moves faster than the other, the pressure has to go somewhere —
            into rents, prices, and crowding.
          </p>

          <div className="mt-10">
            <PlatesHero ratio={peakRatio} />
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <SectionPill href="#flow"  color={C.people} label="The two forces" />
            <SectionPill href="#gap"   color={C.gap}    label="The accumulated gap" />
            <SectionPill href="#squeeze" color={C.homes} label="Where the pressure goes" />
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16">

        {loading && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-8 animate-pulse">
            <div className="h-4 w-40 bg-ink-200 rounded mb-6" />
            <div className="h-20 w-64 bg-ink-200 rounded" />
          </div>
        )}

        {error && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 mb-8 text-ink-500">
            Could not load live data — please try again shortly.
          </div>
        )}

        {!loading && (
          <>
            {/* ── Headline gap ── */}
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
                 style={{ borderLeft: `4px solid ${C.gap}` }}>
              <div className="kicker text-copper-600 mb-6">
                Cumulative gap since 2011 · people added beyond housing capacity
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {latestCumulative > 0 ? '+' : ''}{fmtShort(latestCumulative)}
              </div>
              <p className="text-[14px] text-ink-500 mt-3 max-w-xl">
                That's roughly <strong>{fmt(latestCumulative)}</strong> more people than the homes
                built over the same period could accommodate — equivalent to a shortfall of about{' '}
                <strong>{fmt(homesNeeded)}</strong> dwellings at {PERSONS_PER_DWELLING} people each.
              </p>

              <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="kicker text-ink-500" style={{ fontSize: 11 }}>Peak pressure year</span>
                  <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                    {peakYear?.year}
                  </span>
                </div>
                {peakRatio && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="kicker text-ink-500" style={{ fontSize: 11 }}>People per home that year</span>
                    <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                      {peakRatio.toFixed(1)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* ════ SECTION 1 — THE TWO FORCES ════ */}
            <div id="flow" className="scroll-mt-8">
              <h2 className="font-serif text-[28px] text-ink-800 mb-2 mt-12">The two forces, year by year</h2>
              <p className="text-[14px] text-ink-500 mb-6 max-w-2xl leading-relaxed">
                Red bars show how many people Canada added each year. The teal line shows how many
                those year's housing starts could actually house (homes built × {PERSONS_PER_DWELLING}).
                When the bar towers over the line, the gap widens.
              </p>

              <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
                <ResponsiveContainer width="100%" height={340}>
                  <ComposedChart data={flow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="year" tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                           axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtShort} tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                           axisLine={false} tickLine={false} width={44} />
                    <Tooltip content={<FlowTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
                    <Bar dataKey="people" name="People added" fill={C.people} radius={[3,3,0,0]} maxBarSize={34} />
                    <Line type="monotone" dataKey="capacity" name="People housed"
                          stroke={C.homes} strokeWidth={2.5} dot={{ r: 3, fill: C.homes }} />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="flex gap-6 mt-5">
                  <div className="flex items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: C.people, display: 'inline-block' }} />
                    <span className="text-[12px] text-cream-100/60">People added (annual population growth)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{ width: 14, height: 3, borderRadius: 2, background: C.homes, display: 'inline-block' }} />
                    <span className="text-[12px] text-cream-100/60">People those homes can house</span>
                  </div>
                </div>
              </div>

              <p className="text-[12px] text-ink-400 italic mb-12">
                Population growth includes both permanent immigrants and the temporary residents
                (students, workers) who drove most of the 2022–2024 surge. In 2023 alone Canada added
                1.27 million people — the fastest growth since 1957 — while housing starts stayed near
                their long-run average. By late 2025, policy changes reversed the temporary-resident
                inflow and population growth turned slightly negative.
              </p>
            </div>

            {/* ════ SECTION 2 — THE ACCUMULATED GAP ════ */}
            <div id="gap" className="scroll-mt-8">
              <h2 className="font-serif text-[28px] text-ink-800 mb-2">The accumulated gap</h2>
              <p className="text-[14px] text-ink-500 mb-6 max-w-2xl leading-relaxed">
                Each year's shortfall doesn't disappear — it stacks. This is the running total of
                people added beyond what new housing could absorb, building since 2011.
              </p>

              <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={cumulative} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gapGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={C.gap} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={C.gap} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="year" tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                           axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtShort} tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                           axisLine={false} tickLine={false} width={44} />
                    <Tooltip content={<GapTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)' }} />
                    <Area type="monotone" dataKey="cumulative" stroke={C.gap} strokeWidth={2.5}
                          fill="url(#gapGrad)" dot={false} activeDot={{ r: 4, fill: C.gap }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[12px] text-ink-400 italic mb-12">
                The curve steepens sharply through 2022–2024, the years of record population growth,
                then begins to flatten as growth slows. A flattening curve means the gap has stopped
                growing — not that it has closed.
              </p>
            </div>

            {/* ════ SECTION 3 — WHERE THE PRESSURE GOES ════ */}
            <div id="squeeze" className="scroll-mt-8">
              <h2 className="font-serif text-[28px] text-ink-800 mb-2">Where the pressure goes</h2>
              <p className="text-[14px] text-ink-500 mb-6 max-w-2xl leading-relaxed">
                When demand outpaces supply, the gap doesn't vanish — it shows up as a tighter rental
                market and higher costs. Here's the vacancy rate over the same period: lower means harder
                to find a home.
              </p>

              {housing?.vacancy?.canada_history?.length > 0 && (
                <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
                  <h3 className="font-serif text-[22px] text-cream-100 mb-2">Rental vacancy rate</h3>
                  <p className="text-[13px] text-cream-100/50 mb-8">
                    A market is considered balanced at around 3%. Below that, renters compete and
                    rents rise faster than inflation.
                  </p>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={housing.vacancy.canada_history.map(h => ({
                      year: String(h.year), rate: h.vacancy_rate,
                    }))} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="vacGrad2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.homes} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={C.homes} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="year" tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                             axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={v => `${v}%`} tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                             axisLine={false} tickLine={false} width={40} />
                      <Tooltip
                        formatter={v => [`${v}%`, 'Vacancy']}
                        contentStyle={tipBox} cursor={{ stroke: 'rgba(255,255,255,0.08)' }} />
                      <ReferenceLine y={3} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4"
                        label={{ value: 'balanced (3%)', fill: 'rgba(232,228,218,0.5)', fontSize: 11, position: 'insideTopRight' }} />
                      <Area type="monotone" dataKey="rate" stroke={C.homes} strokeWidth={2.5}
                            fill="url(#vacGrad2)" dot={false} activeDot={{ r: 4, fill: C.homes }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* The takeaway cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 mb-8">
                <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                     style={{ borderLeft: `4px solid ${C.people}` }}>
                  <div className="kicker text-copper-600 mb-3">The push</div>
                  <p className="text-[13px] text-ink-600 leading-relaxed">
                    Canada's population grew by 3.2% in 2023 — the fastest in 66 years — overwhelmingly
                    from international migration, and mostly temporary residents rather than permanent ones.
                  </p>
                </div>
                <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                     style={{ borderLeft: `4px solid ${C.homes}` }}>
                  <div className="kicker text-copper-600 mb-3">The resistance</div>
                  <p className="text-[13px] text-ink-600 leading-relaxed">
                    Housing starts have hovered near 250,000 a year — enough for roughly 600,000 people
                    annually, far short of the 1.27M added at the 2023 peak.
                  </p>
                </div>
                <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                     style={{ borderLeft: `4px solid ${C.gap}` }}>
                  <div className="kicker text-copper-600 mb-3">The fault line</div>
                  <p className="text-[13px] text-ink-600 leading-relaxed">
                    The accumulated gap doesn't reset. Even as population growth reverses in 2025, the
                    housing built during the surge years can't retroactively catch up.
                  </p>
                </div>
              </div>
            </div>

            {/* ── Method & sources ── */}
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8 mb-8">
              <h3 className="font-semibold text-[15px] text-ink-800 mb-3">How this is calculated</h3>
              <p className="text-[13px] text-ink-500 leading-relaxed mb-3">
                "People added" is total annual population change from Statistics Canada's Demographic
                Estimates Program — this includes births, deaths, permanent immigrants, and the net change
                in non-permanent residents (students and temporary workers). It is deliberately broader
                than permanent-resident admissions alone, because temporary migration drove most of the
                recent surge.
              </p>
              <p className="text-[13px] text-ink-500 leading-relaxed">
                "People housed" converts each year's housing starts into capacity using an average of{' '}
                {PERSONS_PER_DWELLING} persons per dwelling (StatCan). This is a simplification — not every
                start completes in the same year, and household sizes vary — but it gives an honest,
                consistent way to compare the two forces on the same scale.
              </p>
            </div>

            <div className="text-[14px] text-ink-500 leading-relaxed">
              <strong className="text-ink-700">Sources:</strong>{' '}
              Statistics Canada Demographic Estimates Program (population growth, persons per dwelling);
              StatCan Table 34-10-0126-01 (housing starts); CMHC Rental Market Survey (vacancy rates);
              IRCC Open Data (permanent resident admissions).
              <a href="https://www150.statcan.gc.ca/n1/daily-quotidien/251217/dq251217b-eng.htm"
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1">
                Latest population estimates <ExternalLink size={11} />
              </a>
            </div>
            <div className="mt-4 text-[13px] text-ink-400 italic max-w-2xl">
              Housing starts update annually. Population estimates are released quarterly by Statistics
              Canada, roughly three months after each reference date.
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
